import { useAuth } from '@/contexts/AuthContext'
import type { ChatTurnDebug } from '@/types/chat'

function formatBytes(bytes: number): string {
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB']
  let value = bytes
  let unitIndex = 0
  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024
    unitIndex += 1
  }
  const precision = unitIndex === 0 ? 0 : 1
  return `${value.toFixed(precision)} ${units[unitIndex]}`
}

function buildCaption(debug: ChatTurnDebug): string {
  const parts: string[] = []
  if (debug.bq_bytes_billed != null) {
    parts.push(`scanned ${formatBytes(debug.bq_bytes_billed)}`)
  } else if (debug.bq_dry_run_bytes != null) {
    parts.push(`dry-run ${formatBytes(debug.bq_dry_run_bytes)}`)
  }
  if (debug.latency_ms != null) {
    parts.push(`${debug.latency_ms} ms`)
  }
  return parts.join(' · ')
}

interface ViewQueryPanelProps {
  debug: ChatTurnDebug | null | undefined
}

export default function ViewQueryPanel({ debug }: ViewQueryPanelProps) {
  const { user } = useAuth()

  if (!debug) {
    return null
  }

  const caption = buildCaption(debug)

  return (
    <details className="text-xs text-gray-500">
      <summary className="cursor-pointer underline-offset-2 transition hover:text-gray-700 hover:underline">
        View query
      </summary>
      <div className="mt-2 space-y-1.5 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-gray-600">
        {caption ? <p className="text-gray-500">{caption}</p> : null}
        {debug.generated_sql ? (
          <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[11px] text-gray-800">
            {debug.generated_sql}
          </pre>
        ) : (
          <p className="italic text-gray-500">No SQL generated</p>
        )}
        <p className="font-mono text-[11px] text-gray-400">log_id: {debug.log_id}</p>
        {user?.is_super_admin && debug.error_message ? (
          <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-[11px] text-red-700">
            {debug.error_message}
          </pre>
        ) : null}
      </div>
    </details>
  )
}
